import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchSettings } from '../api/settingsApi';

const SettingsContext = createContext({
  settings: null,
  loading: true,
  error: '',
  refresh: async () => {},
  setSettings: () => {},
});

const LS_KEY = 'facto_site_settings_v1';
const LS_MAX_AGE_MS = 60_000; // small cache; backend also caches briefly

function readCached() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data || !parsed?.ts) return null;
    if (Date.now() - parsed.ts > LS_MAX_AGE_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCached(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // ignore
  }
}

function applyFavicon(url) {
  if (!url) return;
  const existing = document.querySelector("link[rel~='icon']");
  const link = existing || document.createElement('link');
  link.rel = 'icon';
  link.href = url;
  if (!existing) document.head.appendChild(link);
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => readCached());
  const [loading, setLoading] = useState(!settings);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const s = await fetchSettings();
      setSettings(s || null);
      writeCached(s || null);
    } catch (e) {
      setError(e?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (settings?.favicon_url) applyFavicon(settings.favicon_url);
    if (settings?.site_name) document.title = settings.site_name;
  }, [settings?.favicon_url, settings?.site_name]);

  const value = useMemo(
    () => ({
      settings,
      loading,
      error,
      refresh,
      setSettings: (s) => {
        setSettings(s);
        writeCached(s);
      },
    }),
    [settings, loading, error, refresh]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  return useContext(SettingsContext);
}

